import { useSendConversationWhatsAppTemplate } from 'src/modules/communications/hooks/message-box/whatsapp/use-send-conversation-whatsapp-template';
import { useState, useRef, useCallback, useEffect } from 'react';
import type React from 'react';
import { useAlert } from 'src/contexts/alert-context';
import {
  MessageStatus,
  ConversationChannel,
  WhatsAppMessageType,
} from 'src/api/generated';
import { useSendConversationWhatsAppMessage } from 'src/modules/communications/hooks/message-box/whatsapp/use-send-conversation-whatsapp-message';
import type { OptionalOptimisticData } from 'src/modules/shared/messaging/types/whatsapp-message-context.type';
import { createWhatsAppMessageCacheUpdate } from 'src/modules/communications/utils/cache/whatsapp-update.util';
import { MessageInput } from 'src/modules/shared/messaging/components/molecules/message-input';
import { ComposerEmojiPicker } from 'src/modules/shared/messaging/components/molecules/composer-emoji-picker';
import type { MessageInputRef } from 'src/modules/shared/messaging/types/message-input.types';
import { WhatsAppTemplateModal } from 'src/modules/messaging/whatsapp/components/organisms/templates/template-modal';
import { useMessagingConversation } from '../../../../../../shared/messaging/hooks/api/conversations';
import { useWhatsAppSessionActive } from 'src/modules/communications/hooks/message-box/whatsapp/use-session-active';
import { useTemplateReplyStatus } from 'src/modules/communications/hooks/message-box/whatsapp/use-template-reply-status';
import { WhatsAppSessionComposer } from './whatsapp-session-composer';
import { FilePreview } from '../../../whatsapp-media/file-preview';
import { useWhatsAppMediaUpload } from 'src/modules/communications/hooks/message-box/whatsapp/media-upload/media-upload';
import { WhatsAppActionBar } from './action-bar';
import {
  baseInputClasses,
  inputVariantClasses,
} from 'src/modules/shared/styles/component.styles';
import TemplateListModal from 'src/modules/shared/messaging/components/organisms/templates/template-list-modal';
import {
  WhatsAppMediaType,
  validateWhatsAppMediaFile,
  createValidationErrorMessage,
} from 'src/modules/communications/utils/whatsapp/media.util';
import { getPastedImageFile } from 'src/modules/shared/utils/clipboard.util';
import { mapMediaTypeToAttachmentType } from 'src/modules/communications/utils/whatsapp/media-mapping.util';
import { useWritingEnterKeyPreference } from 'src/modules/shared/hooks/use-writing-enter-key-preference';
import { useAddTestConversationInboundMessage } from 'src/modules/shared/hooks/api/crm/test-conversations.mutation';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import {
  IntegrationAddressPermission,
  IntegrationPermission,
  TestConversationPermission,
} from 'src/modules/shared/types/permissions.type';
import { useSenderAvailability } from 'src/modules/shared/hooks/api/messaging/sender-availability';
import { SenderAvailabilityResponse } from 'src/api/generated';
import { getWhatsAppSendErrorMessage } from 'src/modules/shared/utils/integrations/whatsapp-send-error.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { normalizeWhatsAppPhoneNumber } from 'src/modules/shared/utils/formatting/whatsapp.util';
import { WhatsAppConnectionUnavailableGuidance } from 'src/modules/shared/messaging/components/molecules/whatsapp/connection-unavailable-guidance';
import type { WhatsAppMessageBoxProps } from 'src/modules/communications/types/whatsapp/message-box.types';

export const WhatsAppMessageBox: React.FC<WhatsAppMessageBoxProps> = ({
  heightBoundary,
  inlinePanelContainerRef,
  messageContent,
  onMessageContentChange: setMessageContent,
  isSendingMessage: isConversationSending,
  onMessageSendStart,
  toAddress,
  fromAddress,
  toAddressId,
  fromAddressId,
  contactId,
  funnelConversationId,
  funnelId,
  channelSession,
  loading,
  isTestConversation = false,
  isReplyingAsContact = false,
  onReplyingAsContactChange,
}) => {
  const { showAlert } = useAlert();
  const { t } = useI18n();
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isQuickTemplateModalOpen, setIsQuickTemplateModalOpen] =
    useState(false);
  const messageInputRef = useRef<MessageInputRef>(null);
  const emojiPanelRef = useRef<HTMLDivElement>(null);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const submittedFileId = useRef<string | null>(null);
  const { resolvedBehavior } = useWritingEnterKeyPreference();
  useEffect(() => {
    setIsEmojiOpen(false);
  }, [funnelConversationId, fromAddress, toAddress]);
  const { hasPermission: canManageTestConversations } = useHasPermissions(
    TestConversationPermission.Manage
  );
  const { hasPermission: canOpenIntegrationsSettings } = useHasPermissions([
    IntegrationPermission.Read,
    IntegrationAddressPermission.Read,
  ]);

  const { conversation: messagingConversation } = useMessagingConversation(
    channelSession?.messagingConversationId ?? '',
    !!channelSession?.messagingConversationId
  );

  const { sendMessage: createWhatsAppMessage } =
    useSendConversationWhatsAppMessage({
      onSuccess: (message, context) => {
        createWhatsAppMessageCacheUpdate(message, context);
      },
      onError: (error, context) => {
        console.error('Error sending message:', error);
        if (context.optimisticMessage) {
          createWhatsAppMessageCacheUpdate(
            {
              ...context.optimisticMessage,
              status: MessageStatus.FAILED,
            },
            context
          );
        }
        showAlert(
          getWhatsAppSendErrorMessage(
            error,
            t('communications.messageBox.whatsapp.sendError'),
            t('communications.messageBox.whatsapp.connectionUnavailable')
          ),
          'error'
        );
      },
      onUpdate: createWhatsAppMessageCacheUpdate,
    });
  const isSendingMessage = isConversationSending;
  const { sendTemplate, isPending: isTemplatePending } =
    useSendConversationWhatsAppTemplate({
      fromPhoneNumber: fromAddress,
      toPhoneNumber: toAddress,
      funnelAddressId: fromAddressId,
      contactAddressId: toAddressId,
      contactId,
      funnelConversationId,
      funnelId,
      channelSession,
    });

  const { addInboundMessage, loading: isSimulatingInboundMessage } =
    useAddTestConversationInboundMessage();

  const { mainParticipant } = messagingConversation ?? {};
  const fromPhoneNumber = fromAddress ?? mainParticipant?.address;
  const mainParticipantMatchesSelectedSender = Boolean(
    fromPhoneNumber &&
      mainParticipant?.address &&
      normalizeWhatsAppPhoneNumber(fromPhoneNumber) ===
        normalizeWhatsAppPhoneNumber(mainParticipant.address)
  );
  const fromPhoneNumberId = mainParticipantMatchesSelectedSender
    ? mainParticipant?.providerId
    : undefined;
  const senderAvailabilityQuery = fromPhoneNumberId
    ? {
        channel: ConversationChannel.WHATSAPP,
        senderId: fromPhoneNumberId,
      }
    : {
        channel: ConversationChannel.WHATSAPP,
        sender: fromPhoneNumber ?? '',
      };
  const {
    data: availability,
    loading: availabilityLoading,
    error: availabilityError,
  } = useSenderAvailability(senderAvailabilityQuery, !isTestConversation);
  const contextLoading = Boolean(loading) || availabilityLoading;
  const sessionActive = useWhatsAppSessionActive(channelSession?.endTime);
  const { sendAndWaitForReply, waitingForReply } = useTemplateReplyStatus({
    sendTemplate,
    conversationId: funnelConversationId,
    fromAddress: fromPhoneNumber,
    toAddress,
    endTime: channelSession?.endTime,
  });
  const connectionBlocked =
    !isTestConversation &&
    (availabilityLoading ||
      Boolean(availabilityError) ||
      availability?.state !== SenderAvailabilityResponse.state.AVAILABLE);
  const connectionUnavailable =
    connectionBlocked && !availabilityLoading && !availabilityError;
  const canUseTestTransport =
    isTestConversation && Boolean(fromPhoneNumber) && Boolean(toAddress);

  const {
    selectedFile,
    selectedMediaType,
    uploadedFileId,
    uploadStep,
    uploadError,
    isUploading,
    handleFileSelect,
    handleFileValidationError,
    handleRemoveFile,
    mapMediaTypeToMessageType,
  } = useWhatsAppMediaUpload({
    fromPhoneNumber,
    onUploadError: (error) => showAlert(error, 'error'),
  });

  useEffect(() => {
    if (!uploadedFileId) submittedFileId.current = null;
  }, [uploadedFileId]);

  useEffect(() => {
    if (!isReplyingAsContact) {
      return;
    }

    handleRemoveFile();
    setIsTemplateModalOpen(false);
    setIsQuickTemplateModalOpen(false);
  }, [handleRemoveFile, isReplyingAsContact]);

  const createPreviewUrl = useCallback((file: File): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.readAsDataURL(file);
    });
  }, []);

  const isSendBlocked =
    Boolean(loading) ||
    isSendingMessage ||
    isSimulatingInboundMessage ||
    !fromPhoneNumber ||
    !toAddress ||
    (!isReplyingAsContact &&
      (connectionBlocked ||
        isUploading ||
        uploadStep === 'uploading' ||
        uploadStep === 'error' ||
        (!canUseTestTransport && !sessionActive)));

  const handleSimulateInboundMessage = async (content: string) => {
    const attempt = onMessageSendStart(content);
    if (!attempt) return;
    try {
      await addInboundMessage({
        conversationId: funnelConversationId,
        payload: { content },
      });
      attempt.succeed();
    } catch (error) {
      attempt.fail();
      showAlert(
        getWhatsAppSendErrorMessage(
          error,
          t('communications.messageBox.whatsapp.sendError'),
          t('communications.messageBox.whatsapp.connectionUnavailable')
        ),
        'error'
      );
    }
  };

  const handleSendMessage = async (content: string) => {
    if (isSendBlocked) {
      return;
    }

    if (isReplyingAsContact) {
      await handleSimulateInboundMessage(content);
      return;
    }

    if (uploadedFileId && submittedFileId.current === uploadedFileId) return;
    const attempt = onMessageSendStart(content);
    if (!attempt) return;

    const messagingConversationId =
      channelSession?.messagingConversationId ?? undefined;

    if (uploadedFileId && selectedMediaType) {
      const optimisticData: OptionalOptimisticData = {
        attachmentType: mapMediaTypeToAttachmentType(selectedMediaType),
        mediaType: selectedMediaType,
        fileName: selectedFile?.name,
        fileSize: selectedFile?.size,
        conversationId: messagingConversationId,
        contactId,
        funnelConversationId,
        funnelId,
        sessionId: channelSession?.id,
      };

      if (selectedFile && selectedMediaType === WhatsAppMediaType.IMAGE) {
        try {
          optimisticData.filePreviewUrl = await createPreviewUrl(selectedFile);
        } catch {
          attempt.fail();
          return;
        }
      }

      createWhatsAppMessage({
        content,
        fromPhoneNumber,
        toPhoneNumber: toAddress,
        type: mapMediaTypeToMessageType(selectedMediaType),
        fileId: uploadedFileId,
        optimisticData,
      });
    } else {
      createWhatsAppMessage({
        content,
        fromPhoneNumber,
        toPhoneNumber: toAddress,
        type: WhatsAppMessageType.TEXT,
        optimisticData: {
          conversationId: messagingConversationId,
          contactId,
          funnelConversationId,
          funnelId,
          sessionId: channelSession?.id,
        },
      });
    }
    submittedFileId.current = uploadedFileId;
    handleRemoveFile();
    attempt.succeed();
    messageInputRef.current?.focus();
  };

  const canReplyAsContact = canManageTestConversations && isTestConversation;
  const handleReplyAsContactChange =
    onReplyingAsContactChange ?? (() => undefined);

  const getPlaceholder = () => {
    if (isReplyingAsContact) {
      return 'Escribe el mensaje entrante del contacto...';
    }

    if (selectedFile) {
      return 'Escribe un mensaje para acompañar el archivo...';
    }

    return t('communications.messageBox.whatsapp.compactPlaceholder');
  };

  const handleSendClick = () => {
    if (messageContent.trim() || uploadedFileId) {
      void handleSendMessage(messageContent);
    }
  };

  const handleInsertSlash = () => {
    const isCommandBarOpen = messageContent.startsWith('/');
    const newContent = isCommandBarOpen ? messageContent : '/' + messageContent;
    if (!isCommandBarOpen) {
      setMessageContent(newContent);
    }
    messageInputRef.current?.setCursorPosition(newContent.length);
  };

  const containerClasses = [
    'relative',
    ...baseInputClasses,
    inputVariantClasses.primary,
    'focus-within:border-primary-500',
    'rounded-3xl',
  ].join(' ');

  const isInputDisabled =
    !contextLoading &&
    (connectionBlocked ||
      (!isReplyingAsContact && !canUseTestTransport && !sessionActive));

  useEffect(() => {
    if (isInputDisabled) setIsEmojiOpen(false);
  }, [isInputDisabled]);

  const handlePaste: React.ClipboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (isInputDisabled || contextLoading || isUploading || isSendingMessage) {
      return;
    }

    const file = getPastedImageFile(e.clipboardData);
    if (!file) return;

    e.preventDefault();

    const validation = validateWhatsAppMediaFile(file);
    if (!validation.isValid) {
      handleFileValidationError(createValidationErrorMessage(validation));
      return;
    }

    void handleFileSelect(file, validation.mediaType);
  };

  return (
    <div className="flex min-w-0 flex-col gap-2 [@container(max-height:280px)]:gap-1">
      {availabilityError && !availabilityLoading ? (
        <Alert
          variant="warning"
          size="sm"
          message={t('communications.messageBox.whatsapp.availabilityError')}
        />
      ) : connectionUnavailable ? (
        <Alert
          variant="warning"
          size="sm"
          message={
            <WhatsAppConnectionUnavailableGuidance
              canOpenIntegrationsSettings={canOpenIntegrationsSettings}
            />
          }
        />
      ) : null}

      {selectedFile && selectedMediaType && (
        <div className="flex justify-start">
          <div className="w-full max-w-xs sm:max-w-sm md:max-w-md">
            <FilePreview
              file={selectedFile}
              mediaType={selectedMediaType}
              onRemove={handleRemoveFile}
              className="w-full"
              uploadStep={uploadStep}
              uploadError={uploadError}
            />
          </div>
        </div>
      )}

      <WhatsAppSessionComposer
        loading={loading}
        connectionLoading={availabilityLoading}
        connectionAvailable={
          availability?.state === SenderAvailabilityResponse.state.AVAILABLE
        }
        isTestConversation={isTestConversation}
        sessionActive={sessionActive}
        fromAddress={fromPhoneNumber}
        toAddress={toAddress}
        expired={Boolean(channelSession?.endTime)}
        waitingForReply={waitingForReply}
        disabled={connectionBlocked}
        onChooseTemplate={() => setIsTemplateModalOpen(true)}
      >
        <div>
          <div className={containerClasses}>
            <WhatsAppActionBar
              inputActions={
                <ComposerEmojiPicker
                  inputRef={messageInputRef}
                  mobileContainerRef={inlinePanelContainerRef ?? emojiPanelRef}
                  heightBoundary={heightBoundary}
                  disabled={
                    isInputDisabled ||
                    isSendingMessage ||
                    isSimulatingInboundMessage
                  }
                  open={isEmojiOpen}
                  onOpenChange={setIsEmojiOpen}
                />
              }
              onSharePropertyClick={() =>
                messageInputRef.current?.openPropertyPicker()
              }
              onFileSelect={handleFileSelect}
              onFileValidationError={handleFileValidationError}
              onTemplateClick={() => setIsTemplateModalOpen(true)}
              onQuickTemplateClick={() => setIsQuickTemplateModalOpen(true)}
              onInsertSlash={handleInsertSlash}
              onSendClick={handleSendClick}
              isLoading={
                Boolean(loading) ||
                connectionBlocked ||
                !fromPhoneNumber ||
                !toAddress
              }
              isSendingMessage={isSendingMessage || isSimulatingInboundMessage}
              isUploading={isUploading}
              uploadStep={uploadStep}
              sessionActive={sessionActive}
              channelSession={channelSession}
              isTestConversation={isTestConversation}
              showReplyAsContactToggle={canReplyAsContact}
              isReplyingAsContact={isReplyingAsContact}
              onReplyAsContactChange={handleReplyAsContactChange}
            >
              <div className="min-w-0 flex-1 [&_textarea]:resize-none [&_textarea]:border-0 [&_textarea]:bg-transparent [&_textarea]:px-1 [&_textarea]:overscroll-contain [&_textarea]:focus:outline-none">
                <MessageInput
                  ref={messageInputRef}
                  value={messageContent}
                  onChange={setMessageContent}
                  onSendMessage={handleSendMessage}
                  isSendingMessage={isSendingMessage || isUploading}
                  placeholder={getPlaceholder()}
                  disabled={isInputDisabled}
                  onPaste={handlePaste}
                  enterBehavior={resolvedBehavior}
                  rows={1}
                  maxRows={8}
                  heightBoundary={heightBoundary}
                  focusRetentionBoundary={heightBoundary?.containerRef}
                  suggestionsSuspended={isEmojiOpen}
                  onFocus={() => setIsEmojiOpen(false)}
                />
              </div>
            </WhatsAppActionBar>
          </div>

          {!inlinePanelContainerRef && (
            <div ref={emojiPanelRef} className="-mx-3 mt-1 empty:hidden" />
          )}
        </div>
      </WhatsAppSessionComposer>

      <WhatsAppTemplateModal
        contextLocked
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        fromPhoneNumberId={fromPhoneNumberId}
        initialPhoneAddress={fromPhoneNumber}
        toNumber={toAddress}
        onSendTemplate={sendAndWaitForReply}
        isSending={isTemplatePending}
      />

      <TemplateListModal
        isOpen={isQuickTemplateModalOpen}
        onClose={() => setIsQuickTemplateModalOpen(false)}
      />
    </div>
  );
};
